import React from 'react';
import { CheckIcon } from 'lucide-react';
import { useFunnel } from '../../contexts/FunnelContext';
import { formatPhone } from '../../utils/format';
import { smsDisclosures } from '../../data/consent';
import { ownershipLine } from '../../data/business';
import type { StepErrors } from '../../utils/validation';
import type { ContactChannel } from '../../types/funnel';
import { Segmented } from './Segmented';
import { StepHeading } from './StepHeading';
import { TextField } from './TextField';
import { HelpAnswers } from './HelpAnswers';
import { LegalLinks } from '../legal/LegalLinks';
function ConsentBox({
  checked,
  onChange,
  label,
  tag,
  error
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  tag: string;
  error?: string;
}) {
  return <div data-id="element-671" data-mp="3v0loikc0d5qf">
      <label className="flex cursor-pointer items-start gap-3 py-2" data-id="element-672" data-mp="3spv9585oy4e">
        <input type="checkbox" className="peer sr-only" checked={checked} onChange={e => onChange(e.target.checked)} aria-invalid={!!error} data-id="element-673" data-mp="3qd4q3oc1ffq" />
        <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md border-2 transition-[background-color,border-color] duration-150 ease-out peer-focus-visible:outline peer-focus-visible:outline-[3px] peer-focus-visible:outline-offset-2 peer-focus-visible:outline-magenta ${checked ? 'border-ink bg-ink text-lime' : error ? 'border-magenta bg-white' : 'border-ink-300 bg-white'}`} aria-hidden="true" data-id="element-674" data-mp="1hducnmyr0n36">
          {checked && <CheckIcon className="h-4 w-4" strokeWidth={3} data-id="element-675" data-mp="1w1p4z28ugpfq" />}
        </span>
        <span className="text-[15px] leading-relaxed text-ink" data-id="element-676" data-mp="1w4l4gv0cbm6a">
          {label} <span className="text-sm font-semibold text-ink-500" data-id="element-677" data-mp="1r9nwlresriuz">({tag})</span>
        </span>
      </label>
      {error && <p className="ml-9 text-sm font-medium text-magenta-dark" data-id="element-678" data-mp="1mab9r6nxebse">{error}</p>}
    </div>;
}
export function StepContact({
  errors
}: {
  errors: StepErrors;
}) {
  const {
    t,
    lang,
    data,
    update
  } = useFunnel();
  const err = (k: string) => errors[k] ? t(errors[k]!) : undefined;
  return <div data-id="element-679" data-mp="2xy3i3rbaia17">
      <StepHeading title={t('contactTitle')} data-id="element-680" data-mp="2xzekpnt01bqq" />
      <p className="-mt-2 mb-6 text-sm text-ink-600" data-id="element-681" data-mp="32woa53ge1nek">{ownershipLine[lang]}</p>
      <div className="grid gap-5 sm:grid-cols-2" data-id="element-682" data-mp="2p59o0nr6p63a">
        <TextField label={t('firstName')} autoComplete="given-name" value={data.firstName} onChange={v => update({
        firstName: v
      })} error={err('firstName')} data-id="element-683" data-mp="2p4i3q931eusb" />
        <TextField label={t('lastName')} autoComplete="family-name" value={data.lastName} onChange={v => update({
        lastName: v
      })} error={err('lastName')} data-id="element-684" data-mp="2f6aobuoecksf" />
        <TextField label={t('phone')} type="tel" inputMode="tel" autoComplete="tel-national" value={data.phone} onChange={v => update({
        phone: formatPhone(v)
      })} error={err('phone')} data-id="element-685" data-mp="2k3u797w1hlqp" />
        <TextField label={t('email')} type="email" autoComplete="email" value={data.email} onChange={v => update({
        email: v
      })} error={err('email')} data-id="element-686" data-mp="2a80ubneerq8e" />
      </div>

      {/* SMS consent: two separate, optional, unchecked boxes. Nothing else on this page sets them. */}
      <fieldset className="mt-6 rounded-xl bg-white p-4 ring-1 ring-ink-200 sm:p-5" data-id="element-687" data-mp="1szge6e6hmyym">
        <legend className="sr-only" data-id="element-688" data-mp="1oxmikkb5gl5b">{lang === 'es' ? 'Consentimiento para mensajes de texto' : 'Text message consent'}</legend>
        <ConsentBox checked={data.consentSmsMarketing} onChange={v => update({
        consentSmsMarketing: v
      })} label={smsDisclosures.marketing[lang]} tag={t('consentOptional')} data-id="element-689" data-mp="22psxy46hao3p" />
        <ConsentBox checked={data.consentSmsNonMarketing} onChange={v => update({
        consentSmsNonMarketing: v
      })} label={smsDisclosures.nonMarketing[lang]} tag={t('consentOptional')} data-id="element-690" data-mp="2clmfy32yv9sv" />
        <p className="mt-2 pl-9 text-sm font-medium text-ink-700" data-id="element-691" data-mp="2lnlyx0xjknyg">{smsDisclosures.optionalNote[lang]}</p>
        <LegalLinks lang={lang} newTab className="pl-9 text-sm text-ink-700" data-id="element-692" data-mp="2gnr9l1tf06fi" />
      </fieldset>

      <div className="mt-6" data-id="element-693" data-mp="r8jhzrmk6e75">
        <Segmented<ContactChannel> label={t('channelLabel')} value={data.contactChannel} onChange={v => update({
        contactChannel: v
      })} options={[{
        value: 'call',
        label: t('chCall')
      }, {
        value: 'text',
        label: t('chText')
      }, {
        value: 'email',
        label: t('chEmail')
      }]} data-id="element-694" data-mp="r9ulrrg07ly9" />
      </div>
      <div className="mt-8 space-y-1 border-t border-ink-200 pt-6" data-id="element-695" data-mp="xp1qwbblqi1w">
        <ConsentBox checked={data.consentQuote} onChange={v => update({
        consentQuote: v
      })} label={t('consentQuote')} tag={t('consentRequired')} error={err('consentQuote')} data-id="element-696" data-mp="12oe9gjvfx2yo" />
        <ConsentBox checked={data.consentAiCall} onChange={v => update({
        consentAiCall: v
      })} label={t('consentAi')} tag={t('consentOptional')} data-id="element-697" data-mp="4ykt4jtk6e8f" />
      </div>
      <HelpAnswers screen="stepContact" className="mt-10" data-id="element-698" data-mp="exsf1veczh75" />
    </div>;
}